import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Magnifier } from "components/shared/icons";
import useGlobalSearch from "hooks/useGlobalSearch";
import CategoryTabs from "./CategoryTabs";
import GroupedResults from "./GroupedResults";
import FullListResults from "./FullListResults";
import { LANGUAGE } from "services/general";

const GlobalSearch = ({ isDark = false }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategoryForView, setSelectedCategoryForView] = useState(null);
  const dropdownRef = useRef(null);
  const overlayRef = useRef(null);
  const inputRef = useRef(null);

  // Use the global search hook
  const {
    searchQuery,
    setSearchQuery,
    activeCategories,
    setActiveCategory,
    results,
    loading,
    error,
  } = useGlobalSearch();

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inDropdown = dropdownRef.current?.contains(event.target);
      const inOverlay = overlayRef.current?.contains(event.target);
      const inInput = inputRef.current?.contains(event.target);
      if (!inDropdown && !inOverlay && !inInput) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  // Reset selected category view when category selection changes
  useEffect(() => {
    setSelectedCategoryForView(null);
  }, [activeCategories]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Lock body scroll when full-screen search is open (mobile)
  useEffect(() => {
    if (isOpen && window.innerWidth < 768) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setSelectedCategoryForView(null);
  };

  const handleViewAll = (category) => {
    setSelectedCategoryForView(category);
  };

  // The result is a link and navigates by itself; this only closes the dropdown.
  const handleItemClick = () => {
    setIsOpen(false);
    setSearchQuery("");
  };

  const getCurrentResults = () => {
    if (selectedCategoryForView) {
      return results[selectedCategoryForView] || [];
    }
    if (activeCategories.length === 1) {
      return results[activeCategories[0]] || [];
    }
    return [];
  };

  const getCurrentCategory = () => {
    if (selectedCategoryForView) {
      return selectedCategoryForView;
    }
    if (activeCategories.length === 1) {
      return activeCategories[0];
    }
    return null;
  };

  const showGroupedResults =
    (activeCategories.length === 0 || activeCategories.length >= 2) &&
    !selectedCategoryForView;

  const searchInputEl = (
    <div
      className={`${
        isDark
          ? "bg-white/[0.08] border border-white/[0.12]"
          : "bg-surface border border-border-muted search-shadow"
      } px-2 sm:px-[10px] md:px-[15px] rounded-[10px] w-full justify-between items-center flex`}
    >
      <div className="flex items-center gap-1 sm:gap-[6px] md:gap-[10px] flex-1 min-w-0">
        <Magnifier isWhite={isDark} className="shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={t("search_placeholder")}
          className={`w-full h-[38px] sm:h-[42px] md:h-[44px] focus:outline-none text-[14px] sm:text-[15px] md:text-[16px] leading-[14px] font-medium min-w-0 bg-transparent ${
            isDark
              ? "text-white placeholder:text-white/50"
              : "text-[#666] placeholder:text-[#666]"
          }`}
        />
      </div>
    </div>
  );

  const searchResultsContent = (
    <>
      <div className="p-4 shrink-0">
        <CategoryTabs
          activeCategories={activeCategories}
          onCategoryChange={handleCategoryChange}
        />
      </div>

      <div className="p-4 pt-0 overflow-y-auto overflow-x-hidden flex-1 min-w-0 min-h-0 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
        {loading ? (
          <div className="py-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
            <p className="text-text-muted mt-2">
              {t("globalSearch.loading") || "جاري البحث..."}
            </p>
          </div>
        ) : error ? (
          <div className="py-8 text-center text-danger">{error}</div>
        ) : showGroupedResults ? (
          <GroupedResults
            results={results}
            onViewAll={handleViewAll}
            onItemClick={handleItemClick}
            searchQuery={searchQuery}
          />
        ) : (
          <FullListResults
            items={getCurrentResults()}
            category={getCurrentCategory()}
            onItemClick={handleItemClick}
            searchQuery={searchQuery}
          />
        )}
      </div>
    </>
  );

  return (
    <div className="relative w-full min-w-[120px] max-w-[220px] xsm:max-w-[260px] md:max-w-[340px] lg:max-w-[440px] xl:max-w-[540px] 2xl:max-w-[640px]">
      <div className="w-full">{searchInputEl}</div>

      {isOpen && (
        <div ref={dropdownRef} className="contents">
          {/* Full-screen search overlay on focus (mobile/tablet) - portaled so it appears above logo */}
          {typeof document !== "undefined" &&
            createPortal(
              <div
                ref={overlayRef}
                className={`md:hidden fixed inset-0 z-[300] bg-surface flex flex-col ${
                  LANGUAGE === "ar" ? "left-0 ar" : "right-0"
                }`}
                style={{
                  top: 0,
                  bottom: 0,
                  width: "100%",
                  ...(LANGUAGE === "ar"
                    ? {
                        direction: "rtl",
                        fontFamily: '"Expo-Arabic-Book", sans-serif',
                      }
                    : {}),
                }}
              >
                <div className="p-4 border-b border-gray-100 shrink-0">
                  <div className="flex items-center gap-2">
                    {searchInputEl}
                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      className="shrink-0 p-2 rounded-lg hover:bg-gray-100 text-secondary"
                      aria-label={t("close") || "Close"}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-hidden flex flex-col min-h-0 min-w-0">
                  {searchResultsContent}
                </div>
              </div>,
              document.body
            )}

          {/* Dropdown search (desktop) */}
          <div
            className={`hidden md:flex md:flex-col absolute top-full mt-2 ${
              LANGUAGE === "ar" ? "right-0" : "left-0"
            } w-[340px] lg:w-[440px] xl:w-[540px] 2xl:w-[640px] bg-surface rounded-lg shadow-lg border border-border-light z-[100] max-h-[70vh] min-w-0`}
          >
            {searchResultsContent}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
